import { useCallback } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper.ts";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext.ts";
import { logError } from "@entrilia/shared/logging.ts";
import type { BuildListReportRequest, ReportField } from "../../../../../../../../../api/types/svTypes.ts";
import { useEnhancedBiApiClientProvider } from "../../../../../contexts/ApiClientProviderContext.ts";
import type { FetchRowsPayload } from "@entrilia/shared/components/reporting/text-filter-grid/useGridDataSourceWithRangePaging.ts";
import { useAppDispatch } from "../../../../../store/hooks/useAppDispatch.ts";
import { createDevToolsAction } from "../../../../../store/utils/storeUtils.ts";
import type { ListPageResponse } from "../../utils/prepareListReport.ts";
import { toListRow } from "../../utils/listGridHelper.ts";
import { toSortConfigurations } from "../../utils/listSortHelper.ts";
import type { ListBuildPayload } from "./useListReportConfiguration.ts";

type ListRowsFetcher = (params: FetchRowsPayload<ListBuildPayload>) => Promise<ApiResponse<ListPageResponse>>;

const useListRowsFetcher = (fields: ReportField[]): ListRowsFetcher => {
  const { buildListReport } = useEnhancedBiApiClientProvider();
  const dispatch = useAppDispatch();
  const { sendNotificationError } = useNotificationContext();

  return useCallback(
    async (params: FetchRowsPayload<ListBuildPayload>) => {
      if (!buildListReport) {
        return { success: false };
      }

      const request: BuildListReportRequest = {
        reportId: params.reportId ?? "",
        sessionId: params.sessionId,
        conditions: params.conditions,
        fields: params.fields,
        ledger: params.ledger,
        sort: toSortConfigurations(params.sortModel, fields),
        settings: params.settings,
        range: params.range,
        useQueryLogging: params.useQueryLogging,
      };
      const [response, error] = await withErrorHandling(buildListReport)(request, params.signal);

      if (error) {
        if (error.isCanceledRequest) {
          return { success: false };
        }
        logError(error, "[ListPreviewGrid] fetchRows");
        sendNotificationError(error.message);
        return { success: false, error };
      }
      if (params.useQueryLogging && response.loggingItems !== undefined) {
        dispatch(createDevToolsAction("update", { loggingItems: response.loggingItems }));
      }

      return {
        success: true,
        data: {
          items: response.rows.map(toListRow),
          total: response.total,
          failedMeasures: response.failedMeasures,
          grandTotalItem: toListRow(response.grandTotalItem ?? []),
        },
      };
    },
    [buildListReport, dispatch, fields, sendNotificationError]
  );
};

export default useListRowsFetcher;
